import { clsx } from 'clsx'
import { FileTextIcon } from '@radix-ui/react-icons'
import { invariant } from '@epic-web/invariant'
import { widthAndHeightToAspectRatio } from '@basehub/utils/math/index'
import { BlockChange, MediaBlock } from '@basehub/data/replicache/index'

import { DiffBlockWrapper } from './block-wrapper'
import { DiffBlockTitle } from './block-title'
import { formatBytes } from '~/lib/file-utils'

export const DiffAsset: React.FC<
  React.PropsWithChildren<
    {
      condition: 'added' | 'removed'
      change: BlockChange
      extraContentNode?: ({
        wrapper,
      }: {
        wrapper: ({
          children,
        }: {
          children: React.ReactNode
        }) => React.ReactNode
      }) => React.ReactNode
    } & ({ link: false } | { link: true; parentSourceBlockId: string })
  >
> = ({ condition, change, children, extraContentNode, ...rest }) => {
  const block =
    condition === 'added'
      ? change.current ?? change.previous
      : change.conflicting ?? change.previous

  const dim = Boolean(condition === 'added' && !change.current)

  invariant(
    block?.type === 'media',
    `Block should be a Media block. Was ${block?.type}. ${condition}. ${change}.`
  )
  const hasChanged = assetHasChanged(change.previous as MediaBlock, block)
  const linkProps = rest.link
    ? {
        link: true as const,
        parentSourceBlockId: rest?.parentSourceBlockId as string,
      }
    : { link: false as const }
  let borderColor = 'border-dark-border-solid'
  if (condition === 'added' && change.status === 'created') {
    borderColor = '!border-green-diff/50'
  } else if (
    condition === 'added' &&
    change.status === 'modified' &&
    hasChanged
  ) {
    borderColor = '!border-yellow'
  } else if (change.status === 'deleted' && condition === 'removed') {
    borderColor = '!border-red-elevated'
  }

  return (
    <DiffBlockWrapper className={dim ? 'opacity-50' : ''}>
      <DiffBlockTitle {...linkProps} blockChange={change} condition={condition}>
        {children}
      </DiffBlockTitle>
      <div
        className={clsx('max-w-full overflow-hidden', {
          'opacity-50': change.status === 'deleted' && condition === 'added',
        })}
      >
        {block.value.status === 'uploaded' ? (
          block.value.type === 'audio' ? (
            <audio
              controls
              className={clsx(
                'w-full border border-dashed rounded-lg bg-dark-base-dark',
                borderColor
              )}
              src={block.value.url}
            />
          ) : block.value.type === 'video' ? (
            <div
              className={clsx(
                'w-fit max-w-full overflow-hidden rounded-md border border-border-solid bg-dark-base-dark p-2',
                borderColor
              )}
            >
              <div
                style={{
                  aspectRatio: widthAndHeightToAspectRatio(
                    block.value.width,
                    block.value.height
                  ),
                  width: block.value.width,
                  maxWidth: '100%',
                }}
                className="relative"
              >
                <video
                  src={block.value.url}
                  style={{
                    backgroundImage: `url(data:image/png;base64,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)`,
                    backgroundRepeat: 'repeat',
                    backgroundSize: '16px',
                  }}
                  controls
                  className={clsx(
                    'h-full w-full',
                    hasChanged && {
                      'border-green-diff/30':
                        condition === 'added' && change.status === 'created',
                      'border-yellow':
                        condition === 'added' && change.status === 'modified',
                      'border-red-diff/30': condition === 'removed',
                    }
                  )}
                />
              </div>
            </div>
          ) : block.value.type === 'image' ? (
            <div
              className={clsx(
                'w-fit max-w-full overflow-hidden rounded-md border border-dashed bg-dark-base-dark p-2',
                borderColor
              )}
            >
              <div
                style={{
                  aspectRatio: block.value.width / block.value.height,
                  width: block.value.width,
                  maxWidth: '100%',
                }}
                className="relative"
              >
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  className="w-full"
                  src={block.value.url}
                  alt={block.value.altText ?? ''}
                />
              </div>
            </div>
          ) : block.value.type === 'file' ? (
            <div
              className={clsx(
                'flex items-center self-center gap-4 rounded-lg border border-dashed py-2 pl-4 pr-3 overflow-hidden bg-dark-base-dark',
                borderColor
              )}
            >
              <FileTextIcon
                className="inline-block shrink-0"
                width={20}
                height={20}
              />
              <div className="inline-block shrink-1">
                <p className="text-xs font-medium text-ellipsis whitespace-nowrap">
                  {block.value.fileName}
                </p>
                <p className="text-xxs text-dark-faint text-ellipsis whitespace-nowrap text-nowrap">
                  {formatBytes(block.value.fileSize)}
                </p>
              </div>
            </div>
          ) : null
        ) : (
          <div
            className={clsx(
              '!h-36 border border-dashed border-dark-border-solid text-opacity-0 file:hidden focus:border-solid relative z-30 rounded-lg bg-dark-shade',
              {
                'border-green-elevated':
                  change.status === 'created' && condition === 'added',
                'border-red-elevated':
                  change.status === 'deleted' && condition === 'removed',
              }
            )}
          >
            <p className="absolute z-30 w-full text-xs leading-4 text-center -translate-y-1/2 opacity-75 pointer-events-none top-1/2 text-dark-muted">
              Empty media
            </p>
          </div>
        )}
      </div>
      {extraContentNode &&
        extraContentNode({
          wrapper: ({ children }) => <div className="mt-2">{children}</div>,
        })}
    </DiffBlockWrapper>
  )
}

const assetHasChanged = (previous: MediaBlock, current: MediaBlock) => {
  if (!previous || !current) return false
  if (previous.value.status !== current.value.status) return true
  if (
    previous.value.status === 'uploaded' &&
    current.value.status === 'uploaded'
  ) {
    return Object.entries(previous.value).some(([key, value]) => {
      return value !== current.value[key as keyof MediaBlock['value']]
    })
  }
  return false
}
